import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { GamesModel } from '../types/GamesModel';
import { GamesRequestParams } from '../types/GamesRequestParams';
import GamesAPI from '../api/GamesAPI';

export type GamesStateType = {
  games: GamesModel | null;
  loading: boolean;
  // False until the first getGames settles, so "never fetched" isn't mistaken for an empty result (which
  // would flash the empty-list UI).
  loaded: boolean;
  error?: string;
  // requestId of the latest dispatched getGames/pollGames. On first load the group switcher can fire two
  // fetches close together; handlers below only apply a response whose requestId still matches.
  latestRequestId?: string;
};

export const GamesInitialState: GamesStateType = {
  games: null,
  loading: false,
  loaded: false,
};

export const getGames = createAsyncThunk(
  'getGames',
  async (input: GamesRequestParams, { rejectWithValue, fulfillWithValue }) => {
    try {
      const data = await GamesAPI.getGames(input);
      return fulfillWithValue(data);
    } catch (error) {
      throw rejectWithValue(error.message);
    }
  },
);

// Silent refresh while games are importing: never flips `loading`, so the list doesn't flash a loader.
// A failed poll is swallowed.
export const pollGames = createAsyncThunk(
  'pollGames',
  async (input: GamesRequestParams, { fulfillWithValue }) => {
    const data = await GamesAPI.getGames(input);
    return fulfillWithValue(data);
  },
);

const slice = createSlice({
  name: 'Games',
  initialState: GamesInitialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getGames.pending, (state, action) => {
      state.loading = true;
      state.latestRequestId = action.meta.requestId;
    });
    builder.addCase(getGames.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.latestRequestId) return;
      state.loading = false;
      state.loaded = true;
      state.games = action.payload;
    });
    builder.addCase(getGames.rejected, (state, action) => {
      if (action.meta.requestId !== state.latestRequestId) return;
      state.loading = false;
      state.loaded = true;
      state.error = action.payload as string;
    });
    // Poll leaves `loading`/`error` alone so a background refresh never blanks the table.
    builder.addCase(pollGames.pending, (state, action) => {
      state.latestRequestId = action.meta.requestId;
    });
    builder.addCase(pollGames.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.latestRequestId) return;
      state.games = action.payload;
    });
  },
});

export default slice.reducer;
